// Landing page content data

export const navLinks = [
  { label: "How it works", href: "/#how-it-works" },
  { label: "Features", href: "/#features" },
  { label: "Demo", href: "/#youtube-demo" },
  { label: "FAQ", href: "/#faq" },
];

export const socialProofItems = [
  "Founders",
  "Students",
  "Creators",
  "Researchers",
  "Engineers",
];

export const howItWorksSteps = [
  {
    step: 1,
    title: "Capture",
    description:
      "Save posts, threads, and videos as you normally would across your favorite platforms.",
    icon: "Bookmark",
  },
  {
    step: 2,
    title: "Sync",
    description:
      "Tavlo automatically pulls in your saved content from X, YouTube, LinkedIn, and more.",
    icon: "RefreshCw",
  },
  {
    step: 3,
    title: "Distill",
    description:
      "AI generates clean summaries, key takeaways, and smart tags for every piece.",
    icon: "Sparkles",
  },
  {
    step: 4,
    title: "Organize",
    description:
      "Everything lands in a searchable library with smart folders and collections.",
    icon: "FolderOpen",
  },
  {
    step: 5,
    title: "Replay",
    description:
      "Resurface content through a calm feed and smart reminders when you need it.",
    icon: "Play",
  },
];

export const features = [
  {
    title: "AI Summaries",
    description:
      "Get instant, clear summaries of every saved post, thread, or video.",
    icon: "FileText",
  },
  {
    title: "Smart Tags",
    description:
      "Automatic topic detection and tagging so you never lose track.",
    icon: "Tags",
  },
  {
    title: "Powerful Search",
    description:
      "Find anything instantly with filters by source, topic, creator, or date.",
    icon: "Search",
  },

  {
    title: "Calm Replay Feed",
    description:
      "A peaceful second feed that resurfaces what matters, without the noise.",
    icon: "Repeat",
  },
  {
    title: "Multi-Source Support",
    description:
      "Connect X, YouTube, LinkedIn, Instagram, and more in one place.",
    icon: "Globe",
  },

  {
    title: "Privacy First",
    description:
      "Your data stays yours. No weird growth hacks, just honest tooling.",
    icon: "Shield",
  },
];

// Video demo chapters with timestamps (seconds)
export const videoChapters = [
  {
    id: "inbox",
    title: "Inbox",
    subtitle: "Your personal feed",
    description:
      "Every save flows into a calm, AI-enhanced feed—no algorithmic noise.",
    timestamp: 0,
    stats: { items: 47, unread: 12 },
  },
  {
    id: "library",
    title: "Library",
    subtitle: "Search + categories",
    description:
      "Find anything instantly with powerful search across all your saves.",
    timestamp: 53,
    stats: { items: 284, categories: 18 },
  },
  {
    id: "filters",
    title: "Smart Filters",
    subtitle: "Source, author, tags",
    description:
      "Filter by platform, creator, topic, or custom tags you create.",
    timestamp: 80,
    stats: { sources: 5, tags: 34 },
  },
  {
    id: "collections",
    title: "Collections",
    subtitle: "private or sharable",
    description:
      "Create custom collections to organize saved posts from any platform, then keep them private or share them publicly for others to explore.",
    timestamp: 100,
    stats: { streak: 14, level: 8 },
  },
  {
    id: "discover",
    title: "Discover",
    subtitle: "public collections",
    description:
      "Browse public collections made by other Tavlo users to discover hidden gems and instantly save the best finds to your own library.",
    timestamp: 133,
    stats: { badges: 12, unlocked: 8 },
  },
  {
    id: "profile",
    title: "Profile",
    subtitle: "Badges",
    description:
      "Track your social media journey with streaks, levels, and insights.",
    timestamp: 162,
    stats: { badges: 12, unlocked: 8 },
  },
];

// Video configuration
export const videoConfig = {
  src: "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/tavlo.mp4",
  // Poster frame from video (first frame, optimized)
  poster:
    "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/Screenshot%202026-02-02%20at%204.16.23%20PM.png",
  duration: 356, // approximate duration in seconds
};

// Tavlo Loop steps for signature animation
export const tavloLoopSteps = [
  {
    id: "capture",
    title: "Capture",
    subtitle: "Save what resonates",
    description: "Hit save on any platform. Tavlo grabs the context.",
    icon: "Bookmark",
    detail: "Saved from LinkedIn",
    example: {
      author: "Sarah Chen",
      handle: "@sarahchen",
      content:
        "The best founders I know spend 30% of their time learning. Not consuming—actually synthesizing and applying.",
      platform: "linkedin",
    },
  },
  {
    id: "distill",
    title: "Distill",
    subtitle: "AI extracts the signal",
    description: "Key points, summaries, and insights—automatically.",
    icon: "Sparkles",
    detail: "3 key points • 45 sec read",
    example: {
      summary:
        "Successful founders prioritize active learning over passive consumption, dedicating significant time to synthesis and application.",
      keyPoints: [
        "30% time on learning",
        "Synthesis over consumption",
        "Apply what you learn",
      ],
    },
  },
  {
    id: "organize",
    title: "Organize",
    subtitle: "Smart tags & collections",
    description: "Auto-categorized, searchable, connected.",
    icon: "FolderOpen",
    detail: "Added to 2 collections",
    example: {
      tags: ["productivity", "founders", "learning"],
      collections: ["Startup Wisdom", "Personal Growth"],
    },
  },
  {
    id: "replay",
    title: "Replay",
    subtitle: "Resurface when it matters",
    description: "The right insight, at the right moment.",
    icon: "RotateCcw",
    detail: "You saved this 14 days ago",
    example: {
      resurfaceReason: "Related to your recent saves about productivity",
      savedAgo: "14 days",
    },
  },
];

// Why Tavlo exists content
export const whyTavloContent = {
  headline: "We built Tavlo because we were tired of the junk drawer.",
  points: [
    "You save great content with good intentions.",
    "Then it disappears into a black hole of bookmarks.",
    "Tavlo is different: it helps you actually *use* what you save.",
  ],
  cta: "A calm second feed, built for reuse—not hoarding.",
};

export const testimonials = [
  {
    quote:
      "I rarely leave reviews, but the tavlo Chrome extension genuinely deserves it. It's one of the most useful, well-designed, and brilliantly thought-out tools I've added to my browser.",
    author: {
      name: "Sunay Yadav",
      role: "Avid social media user",
      image: "/testimonials/sunay-yadav.png",
    },
    rating: 5,
  },
  {
    quote:
      "Before finding Tavlo, my saved posts were just a graveyard, never to be seen again. Now with Tavlo, I can consolidate all my saved posts across all my social media accounts, categorize them automatically, and search for any post or topic super easily.",
    author: {
      name: "Taha Shahzad",
      role: "Content Creator",
      image: "/testimonials/taha-shahzad.jpg",
    },
    rating: 5,
  },
  {
    quote:
      "Love using this platform. Helps me save and sort through my social media posts easily. Use it particularly for sorting through and organizing recipes, which otherwise become difficult to find.",
    author: {
      name: "Hasan Shahzad",
      role: "Recipe Enthusiast & Foodie",
      image: "/testimonials/hasan-shahzad.png",
    },
    rating: 5,
  },
  {
    quote:
      "This is an amazing tool. I often see videos, reels, and posts on social media that I want to save for later, but the process is usually so complicated that I end up forgetting about them. Now, with this tool and extension, everything is a breeze.",
    author: {
      name: "Tahmid Jawad",
      role: "Mortgage Broker",
      image: "/testimonials/tahmid-jawad.jpg",
    },
    rating: 5,
  },
  {
    quote:
      "Just started using it and love it already, tired of losing links or struggling to organize them across platforms. Gonna take a couple days to sync up all my saved content, but I can't wait to get it done.",
    author: {
      name: "Abdul Haqani",
      role: "Software Engineer",
      image: "/testimonials/abdul-haqani.jpg",
    },
    rating: 5,
  },
  {
    quote:
      "Super easy to save posts across social media. The best part about this is that Youtube videos dont play any add if I watch it from Tavlo. Loved it!",
    author: {
      name: "Syed Riyadul Mahi",
      role: "University student",
      image: "/testimonials/syed-mahi.png",
    },
    rating: 5,
  },
];

export const faqs = [
  {
    question: "How is Tavlo different from standard browser bookmarks or Pinterest?",
    answer:
      "Standard bookmarks are 'dumb' links that often lead to a 'content graveyard.' Tavlo is an intelligent second brain for social media. It doesn't just store a URL; it extracts the actual content, generates AI summaries, and automatically tags your saves. Unlike Pinterest which is purely visual, Tavlo handles threads, videos, and articles with equal depth, making them searchable and reusable.",
  },
  {
    question: "Can I search my Twitter (X) bookmarks and LinkedIn saves?",
    answer:
      "Yes! This is one of Tavlo's most powerful features. We solve the 'search problem' on social platforms. Once you save a post to Tavlo, our full-text search allows you to find any insight, phrase, or author across your entire library in seconds—even if the original post is hard to find on the native platform.",
  },
  {
    question: "Which social media platforms does Tavlo support?",
    answer:
      "Tavlo is a cross-platform content hub. We provide deep support for X (Twitter), LinkedIn, Instagram, YouTube, Reddit, and TikTok, as well as newsletters and web articles. Our extraction pipeline normalizes content from all these sources into a consistent, readable format.",
  },
  {
    question: "How does the AI summarization and auto-tagging work?",
    answer:
      "When you save a link, Tavlo's AI processes the content to extract the 'signal from the noise.' It generates a concise summary, identifies key takeaways, and assigns smart tags (like #Marketing, #SaaS, or #Productivity). It even categorizes content by intent—whether it's something to Learn, Do, or Reference.",
  },
  {
    question: "Is there a browser extension or mobile app?",
    answer:
      "Yes. We have a Chrome Extension that lets you save content with one click without leaving your feed. For mobile, we offer iOS Shortcuts and Android share integrations that make it easy to 'send to Tavlo' directly from any social app's share sheet.",
  },
  {
    question: "Can I share my collections or use them for team research?",
    answer:
      "Absolutely. Tavlo allows you to create public or private collections—think of them as 'playlists for the internet.' Creative agencies and teams use shared workspaces to curate references, moodboards, and research for clients without the chaos of messy DMs or fragmented link lists.",
  },
  {
    question: "What happens if a social media post is deleted?",
    answer:
      "Tavlo's pipeline is built for the volatility of social media. For many platforms, we persist a snapshot of the text and key assets. This ensures that even if a thread is deleted or a video is taken down, your personal knowledge library remains intact and useful.",
  },
  {
    question: "What is the pricing model? Is there a free trial?",
    answer:
      "Tavlo offers a 30-day generous free trial with unrestricted access to all premium features, including AI summaries and unlimited saves. After the trial, we offer a simple paid tier for individuals and a Team tier for agencies and studios that includes shared workspaces and collaborative collections.",
  },
  {
    question: "How do you protect my privacy and data?",
    answer:
      "Privacy is our foundation. We do not sell your data or use your saved content to train public AI models. Your library is your private intellectual property. We use industry-standard encryption and secure authentication to ensure your 'second brain' stays yours.",
  },
  {
    question: "Is Tavlo better than Notion or Readwise for social media?",
    answer:
      "While Notion is great for notes and Readwise is excellent for highlights from books/articles, neither is purpose-built for the messiness of social media content. Tavlo specializes in extracting value from threads, carousels, and videos—formats that standard clippers often struggle to process correctly.",
  },
];
